import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { formatApproximateMixedFractionLatex as formatMixedFractionLatex } from "../../../../utils/fractionLatex";

/* ========= Utils ========= */
const wrap = (n) => (n < 0 ? `(${n})` : `${n}`);

/* ========= Generator ========= */
const generateRandomPoint = (type) => {
  if (type === "I") return [Math.floor(Math.random() * 9) + 1, Math.floor(Math.random() * 9) + 1];
  if (type === "II") return [-(Math.floor(Math.random() * 9) + 1), Math.floor(Math.random() * 9) + 1];
  if (type === "IV") return [Math.floor(Math.random() * 9) + 1, -(Math.floor(Math.random() * 9) + 1)];
  return [Math.floor(Math.random() * 11) - 5, Math.floor(Math.random() * 11) - 5];
};

export function generate({ mode } = {}) {
  let a, b;

  if (mode === "first_quadrant") {
    a = generateRandomPoint("I");
    b = generateRandomPoint("I");
    while (a[0] === b[0] && a[1] === b[1]) b = generateRandomPoint("I");
  } else if (mode === "second_fourth") {
    a = generateRandomPoint("II");
    b = generateRandomPoint("IV");
    while (a[0] === b[0] && a[1] === b[1]) b = generateRandomPoint("IV");
  } else {
    a = generateRandomPoint();
    b = generateRandomPoint();
    while (a[0] === b[0] && a[1] === b[1]) b = generateRandomPoint();
  }

  const correct = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];

  return {
    type: "MidpointTask",
    data: { a, b },
    answer: correct
  };
}

export function generateTask(options) {
  return generate(options);
}

/* ========= Treść ========= */
export function renderTask(task) {
  const { a, b } = task.data;

  return (
    <p>
      Wyznacz środek odcinka o końcach{" "}
      <InlineMath math={`A = (${a[0]}, ${a[1]})`} /> i{" "}
      <InlineMath math={`B = (${b[0]}, ${b[1]})`} />.
    </p>
  );
}

/* ========= Input (2× FractionInput) ========= */
export function renderInput(_, userInput, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span><InlineMath math={'x_S ='} /></span>
        <FractionInput
          value={userInput?.x || ""}
          onChange={(val) => onChange({ ...userInput, x: val })}
          disabled={disabled}
          label={"x_S"}
        />
      </div>
      <div className="flex items-center gap-2">
        <span><InlineMath math={'y_S ='} /></span>
        <FractionInput
          value={userInput?.y || ""}
          onChange={(val) => onChange({ ...userInput, y: val })}
          disabled={disabled}
          label={"y_S"}
        />
      </div>
    </div>
  );
}

/* ========= Checking (1 pkt za całość) ========= */
export function checkAnswer(task, userInput) {
  const x = userInput?.x?.parsed;
  const y = userInput?.y?.parsed;

  if ([x, y].some(v => v == null || isNaN(v))) return false;

  return (
    Math.abs(x - task.answer[0]) < 0.01 &&
    Math.abs(y - task.answer[1]) < 0.01
  );
}

/* ========= Odpowiedź ========= */
export function renderAnswer(task) {
  const [x, y] = task.answer;
  return (
    <InlineMath
      math={`S = \\left(${formatMixedFractionLatex(x)},\\ ${formatMixedFractionLatex(y)}\\right)`}
    />
  );
}

/* ========= Wyjaśnienie ========= */
export function renderExplanation({ data, answer }) {
  if (!answer || !Array.isArray(answer)) return <div style={{ color: "red" }}>Brak danych odpowiedzi.</div>;

  const [xS, yS] = answer;

  return (
    <div>
      <p><strong>Wyjaśnienie krok po kroku:</strong></p>

      <div style={{ marginBottom: 16 }}>
        <p><strong>Krok 1.</strong> Wzór na środek odcinka:</p>
        <BlockMath math={'S = \\left( \\frac{x_A + x_B}{2},\\ \\frac{y_A + y_B}{2} \\right)'} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <p><strong>Krok 2.</strong> Podstawiamy współrzędne:</p>
        <BlockMath
          math={`S = \\left( \\frac{${wrap(data.a[0])} + ${wrap(data.b[0])}}{2},\\ \\frac{${wrap(
            data.a[1]
          )} + ${wrap(data.b[1])}}{2} \\right)`}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <p><strong>Krok 3.</strong> Liczymy:</p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1rem",
            alignItems: "start",
          }}
        >
          <div>
            <div style={{ textAlign: "center", fontWeight: 600 }}>
              <InlineMath math={'x_S'} />
            </div>
            <BlockMath math={`x_S = \\frac{${wrap(data.a[0])} + ${wrap(data.b[0])}}{2} = ${formatMixedFractionLatex(xS)}`} />
          </div>

          <div>
            <div style={{ textAlign: "center", fontWeight: 600 }}>
              <InlineMath math={'y_S'} />
            </div>
            <BlockMath math={`y_S = \\frac{${wrap(data.a[1])} + ${wrap(data.b[1])}}{2} = ${formatMixedFractionLatex(yS)}`} />
          </div>
        </div>
      </div>

      <p><strong>Odpowiedź:</strong></p>
      <BlockMath math={`S = \\left(${formatMixedFractionLatex(xS)},\\ ${formatMixedFractionLatex(yS)}\\right)`} />
    </div>
  );
}

/* ========= User answer preview ========= */
export function renderUserAnswer(userInput) {
  if (!userInput?.x?.raw || !userInput?.y?.raw) return <i>(brak odpowiedzi)</i>;

  const x = formatLatexPreview(userInput.x.raw, "x_S");
  const y = formatLatexPreview(userInput.y.raw, "y_S");

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <div><InlineMath math={x} /></div>
      <div style={{ marginTop: "0.5rem" }}><InlineMath math={y} /></div>
    </div>
  );
}
